import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ExternalLink, Send } from "lucide-react";
import { contactPolicy } from "@shared/contactPolicy";
import { smsSend } from "@shared/schemas/messages";
import { parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { fmtPhone, fullName } from "@web/lib/format";
import { fmtClock, isFailedStatus, messageStatusLabel, warningMessage } from "@web/lib/formatLeads";
import { useNavAction } from "@web/lib/navAction";
import { useFormValidation, validationUnavailable } from "@web/lib/formValidation";
import { manualMessageFindings, manualSendFeedback, uncertainSendMessage } from "@web/lib/manualMessage";
import { leadApi, quickRepliesQuery, threadQuery, type Message } from "@web/lib/queries-leads";
import { BottomSheet, Bubble, Button, ChipStrip, ConversationLayout, Dock, EmptyState, Inline, Page, Row, RowGroup, SelectChip, Skeleton, Stack, SystemRow, Text, TextArea, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";
import { ConversationList } from "./Messages";
import { smsError } from "./lead/CommsSheets";
import { ContactSheet } from "./sheets/ContactSheet";

/** "2:05 PM · Delivered" (failed messages say why). */
const caption = (m: Message) => `${fmtClock(m.createdAt)} · ${m.direction === "in" ? "Received" : messageStatusLabel(m.status)}${isFailedStatus(m.status) && m.error ? ` — ${m.error}` : ""}`;

/** One conversation (SPEC §6.1 item 11): bubbles in / out with time + status, calls as system rows, SMS reply composer docked at the bottom. */
export function MessageThread() {
  const { contactId } = useParams({ strict: false }) as { contactId: string };
  return <ConversationLayout selected list={<ConversationList />}><ThreadContent key={contactId} /></ConversationLayout>;
}

function ThreadContent() {
  const { contactId } = useParams({ strict: false }) as { contactId: string };
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuery(threadQuery(contactId));
  const t = q.data;
  const policy = contactPolicy(t?.contact ?? null, "sms");
  const quick = useQuery({ ...quickRepliesQuery(t?.openLeadId), enabled: Boolean(t) });
  const [body, setBody] = useState("");
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const input = { contactId, ...(t?.openLeadId ? { leadId: t.openLeadId } : {}), body };
  const parsed = parseInput(smsSend, input, { type: "message" }, "comms.send_sms");
  const validation = useFormValidation(manualMessageFindings("sms", input, t?.contact ?? null));
  const { busy } = validation;
  const edit = (value: string) => validation.edit(() => { setBody(value); setDeliveryError(null); });
  const [links, setLinks] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const bookingId = t?.items.map((m) => m.bookingId).filter(Boolean).pop() ?? null;
  useNavAction({ label: "Open lead or booking", icon: <ExternalLink aria-hidden size={24} />, onClick: () => setLinks(true) });

  // Mark inbound messages read once the thread is on screen.
  useEffect(() => {
    if (!t || !t.items.some((m) => m.direction === "in" && !m.readAt)) return;
    leadApi.markThreadRead(contactId).catch(() => undefined);
  }, [contactId, t]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [t?.items.length]);

  async function send() {
    if (!body.trim() || !t) return;
    if (!policy.allowed) return toast.error(policy.reason);
    if (!parsed.success || !validation.begin("comms.send_sms")) return;
    setDeliveryError(null);
    try {
      const r = await leadApi.sendSms(parsed.data);
      validation.clear();
      const feedback = manualSendFeedback("sms", r.status, r.error);
      if (!feedback.accepted) { setDeliveryError(feedback.message); return; }
      if (warningMessage(r.warning)) toast.show(warningMessage(r.warning)!);
      else toast.show(feedback.message);
      setBody("");
    } catch (e) {
      validation.reject(e);
      setDeliveryError(validationUnavailable(e) ? uncertainSendMessage : smsError(e));
    } finally {
      validation.finish();
    }
  }

  if (q.isError)
    return (
      <Page as="div">
        <EmptyState title="Conversation not found" body={errorMessage(q.error)} />
      </Page>
    );
  if (!t)
    return (
      <Page as="div">
        <Skeleton variant="cards" count={4} />
      </Page>
    );

  const name = fullName(t.contact) || t.contact.phone || t.contact.email || "Contact";
  return (
    <Page as="div">
      <UnsavedChanges dirty={Boolean(body)} pending={busy} />
      <Stack gap={3}>
        <Row title={name} subtitle={[fmtPhone(t.contact.phone), t.contact.email, t.contact.dnc ? "Do not contact" : null].filter(Boolean).join(" · ")} onClick={() => setContactOpen(true)} />
        {t.items.length === 0 ? (
          <EmptyState title="No messages yet" />
        ) : (
          <Stack gap={2}>
            {t.items.map((m) =>
              m.channel === "call" ? (
                <SystemRow key={m.id}>
                  Call · {m.body} · {fmtClock(m.createdAt)}
                </SystemRow>
              ) : (
                <Bubble key={m.id} direction={m.direction === "in" ? "in" : "out"} caption={caption(m)}>
                  {m.channel === "email" && m.subject ? `${m.subject}\n${m.body}` : m.body}
                </Bubble>
              ),
            )}
          </Stack>
        )}
        <div ref={endRef} />
      </Stack>
      <Dock>
        <Stack gap={2}>
          {!policy.allowed && <Text variant="caption" tone="secondary">{policy.reason}</Text>}
          {(body.length > 0 || deliveryError) && <ValidationSummary result={validation.result} local unavailable={validation.unavailable} context="Draft feedback. Recipient and delivery checks run again when you send." />}
          {deliveryError && <div role="alert"><Text variant="callout" tone="danger">{deliveryError}</Text></div>}
          {quick.data && quick.data.length > 0 && (
            <ChipStrip label="Quick replies">
              {quick.data.map((r) => (
                <SelectChip key={r.id} selected={body === r.body} disabled={busy || !policy.allowed} onClick={() => edit(r.body)}>
                  {r.name}
                </SelectChip>
              ))}
            </ChipStrip>
          )}
          <Inline gap={2} align="end">
            <TextArea label={t.contact.phone ? `Text ${fmtPhone(t.contact.phone)}` : "No phone number"} className="flex-1" rows={1} placeholder="Type a reply" value={body} {...validation.field("body")} disabled={busy || !policy.allowed} onChange={(e) => edit(e.target.value)} />
            <Button fullWidth={false} loading={busy} disabled={!body.trim() || !policy.allowed} icon={<Send aria-hidden size={18} />} onClick={send}>
              Send
            </Button>
          </Inline>
        </Stack>
      </Dock>
      <BottomSheet open={links} onClose={() => setLinks(false)} title={name}>
        <RowGroup>
          {t.openLeadId ? <Row title="Open lead" subtitle="Timeline, details, actions" onClick={() => navigate({ to: "/leads/$id", params: { id: t.openLeadId! } })} /> : <Row title="No open lead" subtitle="Add one from Leads if needed" />}
          {bookingId && <Row title="Open booking" onClick={() => navigate({ to: "/bookings/$id", params: { id: bookingId } })} />}
          <Row title="Contact" subtitle="Details, consent, bookings" onClick={() => (setLinks(false), setContactOpen(true))} />
        </RowGroup>
        {!t.openLeadId && !bookingId && (
          <Text variant="caption" tone="tertiary">
            Replies here still reach {name}.
          </Text>
        )}
      </BottomSheet>
      <ContactSheet contactId={contactOpen ? contactId : null} onClose={() => setContactOpen(false)} />
    </Page>
  );
}
